import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useSubmit } from '../../../hooks/useSubmit'
import { addStockCount } from '../../adminApi'
import { newRequestId } from '../../../core/api/http'
import { readLocalJson, writeLocal, writeLocalJson } from '../../../core/storage'
import { countableAt, countDue, countSummary } from '../../../core/rules/stockCount'
import { today } from '../../../core/format/dates'
import type { StockControl } from '../useStock'
import type { StockCountResult, StockLocation } from '../../../core/types/stock'

/** Per artikel: wat Sarah telde, en de stand die ze zag toen ze het invulde. */
type Draft = Record<string, { counted: number; expected: number | null }>

/**
 * Het concept van een telling blijft op dit apparaat staan tot hij geboekt is: een telling in de
 * salon wordt vaak onderbroken (een klant, de telefoon). Het is geen beheerdata, alleen invoer die
 * nog niet verstuurd is; de server krijgt hem pas bij "Telling opslaan".
 */
const draftKey = (location: StockLocation) => `bb-admin-telling-${location}`

export function useStockCount(stock: StockControl, location: StockLocation) {
  const [draft, setDraft] = useState<Draft>(() => readLocalJson<Draft>(draftKey(location), {}))
  const [query, setQuery] = useState('')
  const [onlyOpen, setOnlyOpen] = useState(false)
  const [result, setResult] = useState<StockCountResult | null>(null)
  const { submit, submitting, error } = useSubmit(addStockCount)
  const request = useRef(newRequestId())

  // Andere plek gekozen: het concept van die plek.
  useEffect(() => {
    setDraft(readLocalJson<Draft>(draftKey(location), {}))
    setResult(null)
    request.current = newRequestId()
  }, [location])

  const persist = useCallback(
    (next: Draft) => {
      setDraft(next)
      if (Object.keys(next).length === 0) writeLocal(draftKey(location), '')
      else writeLocalJson(draftKey(location), next)
      // Andere inhoud = een nieuw verzoek.
      request.current = newRequestId()
    },
    [location],
  )

  const all = useMemo(() => countableAt(stock.items ?? [], location), [stock.items, location])
  const term = query.trim().toLowerCase()
  const visible = all.filter((i) => (!term || `${i.name} ${i.brand ?? ''}`.toLowerCase().includes(term)) && (!onlyOpen || !(i.id in draft)))

  const setCounted = (itemId: string, counted: number | undefined) => {
    const next = { ...draft }
    if (counted === undefined) delete next[itemId]
    else next[itemId] = { counted, expected: draft[itemId]?.expected ?? all.find((i) => i.id === itemId)?.levels[location] ?? null }
    persist(next)
  }

  const lines = Object.entries(draft)
    .filter(([itemId]) => all.some((i) => i.id === itemId))
    .map(([itemId, d]) => ({ itemId, ...d }))
  const costOf = (itemId: string) => all.find((i) => i.id === itemId)?.costPriceCents
  const summary = countSummary(lines, costOf)
  const due = countDue(stock.lastCountAt[location], today())

  const save = async () => {
    if (lines.length === 0) return
    const res = await submit({ requestId: request.current, location, lines })
    if (!res) return
    stock.replaceMany(res.items)
    if (res.count.complete) stock.markCounted(location, res.count.at)
    // Het concept is verwerkt. Conflicten (intussen verkocht) staan in het resultaat: die tel je
    // opnieuw, tegen de nieuwe stand.
    persist({})
    setResult(res)
    toast.success(res.count.complete ? 'Telling opgeslagen: alles geteld' : 'Telling opgeslagen', {
      description: res.count.differences === 0 ? 'Alles klopte.' : `${res.count.differences} ${res.count.differences === 1 ? 'verschil' : 'verschillen'} geboekt.`,
    })
  }

  const discard = () => persist({})

  return { all, visible, draft, setCounted, query, setQuery, onlyOpen, setOnlyOpen, lines, summary, due, save, submitting, error, result, dismissResult: () => setResult(null), discard }
}
